import React, { useState, useEffect, useCallback } from 'react';
import { getSubmissionStatusColor } from '../../utils/statusBadge';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardHeader,
  CardBody,
  VStack,
  HStack,
  Badge,
  Button,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Flex,
  Divider,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Input,
  IconButton,
  Tooltip,
  Link,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react';
import {
  SearchIcon,
  CalendarIcon,
  DownloadIcon,
  ViewIcon,
  EditIcon,
  ExternalLinkIcon,
  InfoIcon,
  WarningIcon,
  CheckCircleIcon,
  TimeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@chakra-ui/icons';
import {
  FaVial,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaHourglassHalf,
  FaTruck,
  FaChartLine,
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaCalendarAlt,
  FaFileAlt,
  FaUserMd,
  FaUser,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaIdCard,
  FaShieldAlt,
  FaClipboardCheck,
  FaClipboardList,
  FaSync,
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { useOutletContext, useNavigate } from 'react-router-dom';

const BloodDrawDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailedView, setDetailedView] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const { adminUser } = useOutletContext();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('md', 'dark-lg');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // Define submission statuses with their properties
  const statusConfig = {
    pending: {
      label: 'Pending',
      color: 'yellow',
      icon: FaHourglassHalf,
      description: 'Awaiting processing'
    },
    in_progress: {
      label: 'In Progress',
      color: 'blue',
      icon: FaClipboardList,
      description: 'Currently being processed'
    },
    completed: {
      label: 'Completed',
      color: 'green',
      icon: FaCheckCircle,
      description: 'Successfully completed'
    },
    cancelled: {
      label: 'Cancelled',
      color: 'red',
      icon: FaTimes,
      description: 'Cancelled or rejected'
    }
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select(`
          *,
          phlebotomist_profiles (
            id,
            company_name,
            full_name,
            email,
            phone
          ),
          labs (
            id,
            name,
            address
          ),
          tests (
            id,
            name,
            description,
            cash_price
          ),
          insurances (
            id,
            name,
            type,
            description
          )
        `)
        .order('submitted_at', { ascending: false }));

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching submissions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSubmissions();

    // Real-time sync: auto-update when lab portal changes submission status
    const channel = supabase
      .channel('blood-draw-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'submissions' }, () => {
        fetchSubmissions();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchSubmissions]);

  // Calculate statistics
  const calculateStats = () => {
    const total = submissions.length;
    const statusCounts = {};
    const today = new Date();
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Initialize status counts
    Object.keys(statusConfig).forEach(status => {
      statusCounts[status] = 0;
    });

    // Count submissions by status and time periods
    let todayCount = 0;
    let weekCount = 0;
    let monthCount = 0;

    submissions.forEach(submission => {
      const status = submission.status || 'pending';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      }

      const submittedAt = new Date(submission.submitted_at);
      if (submittedAt.toDateString() === today.toDateString()) {
        todayCount++;
      }
      if (submittedAt >= thisWeek) {
        weekCount++;
      }
      if (submittedAt >= thisMonth) {
        monthCount++;
      }
    });

    return {
      total,
      statusCounts,
      todayCount,
      weekCount,
      monthCount,
      completedCount: statusCounts.completed,
      pendingCount: statusCounts.pending,
      inProgressCount: statusCounts.in_progress,
      cancelledCount: statusCounts.cancelled
    };
  };

  const stats = calculateStats();

  // Filter submissions based on search and filters
  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = 
      submission.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      submission.doctor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      submission.phlebotomist_profiles?.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      submission.tests?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;

    const submittedAt = new Date(submission.submitted_at);
    let matchesDate = true;
    
    if (dateRange === 'today') {
      const today = new Date();
      matchesDate = submittedAt.toDateString() === today.toDateString();
    } else if (dateRange === 'week') {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      matchesDate = submittedAt >= weekAgo;
    } else if (dateRange === 'month') {
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      matchesDate = submittedAt >= monthAgo;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    onOpen();
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: newStatus })
        .eq('id', submissionId);

      if (error) throw error;

      setSubmissions(submissions.map(sub => 
        sub.id === submissionId ? { ...sub, status: newStatus } : sub
      ));

      toast({
        title: 'Status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => getSubmissionStatusColor(status);

  const getStatusIcon = (status) => {
    const IconComponent = statusConfig[status]?.icon || FaClock;
    return <IconComponent />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box px={{ base: 2, md: 8 }} py={8} bg={bgColor} minH="100vh">
      <Flex justify="space-between" align="center" mb={8}>
        <Heading as="h1" size="2xl" fontWeight="bold">
          Blood Draw Dashboard
        </Heading>
        <HStack spacing={4}>
          <Button
            leftIcon={<FaChartLine />}
            colorScheme="blue"
            variant="outline"
            onClick={() => setDetailedView(!detailedView)}
          >
            {detailedView ? 'Simple View' : 'Detailed View'}
          </Button>
          <Button
            leftIcon={<DownloadIcon />}
            colorScheme="green"
            onClick={() => {
              // TODO: Implement export functionality
              toast({
                title: 'Export feature coming soon',
                status: 'info',
                duration: 3000,
                isClosable: true,
              });
            }}
          >
            Export Data
          </Button>
        </HStack>
      </Flex>

      {/* Overview Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={8}>
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Total Submissions</StatLabel>
              <StatNumber fontSize="3xl">{stats.total}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                All time
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Today</StatLabel>
              <StatNumber fontSize="3xl" color="blue.500">{stats.todayCount}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                New today
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>This Week</StatLabel>
              <StatNumber fontSize="3xl" color="green.500">{stats.weekCount}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Last 7 days
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Completed</StatLabel>
              <StatNumber fontSize="3xl" color="green.600">{stats.completedCount}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Successfully completed
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Status Breakdown */}
      <Card bg={cardBg} boxShadow={cardShadow} mb={6}>
        <CardHeader>
          <Heading size="md">Status Breakdown</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
            {Object.entries(statusConfig).map(([status, config]) => (
              <Box key={status} textAlign="center">
                <VStack spacing={2}>
                  <Box
                    p={4}
                    borderRadius="full"
                    bg={`${config.color}.100`}
                    color={`${config.color}.600`}
                  >
                    {React.createElement(config.icon, { size: 24 })}
                  </Box>
                  <VStack spacing={1}>
                    <Text fontWeight="bold" fontSize="2xl">
                      {stats.statusCounts[status] || 0}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {config.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {config.description}
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card bg={cardBg} boxShadow={cardShadow} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <FormControl>
              <FormLabel>Search</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by patient, doctor, or lab..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </FormControl>
            
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                {Object.entries(statusConfig).map(([status, config]) => (
                  <option key={status} value={status}>
                    {config.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Date Range</FormLabel>
              <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Actions</FormLabel>
              <Button
                leftIcon={<FaSync />}
                variant="outline"
                onClick={fetchSubmissions}
                w="full"
              >
                Refresh
              </Button>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Submissions Table */}
      <Card bg={cardBg} boxShadow={cardShadow}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">
              Submissions ({filteredSubmissions.length})
            </Heading>
            <Badge colorScheme="blue" variant="outline">
              {filteredSubmissions.length} of {submissions.length}
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Patient</Th>
                  <Th>Doctor</Th>
                  <Th>Test</Th>
                  <Th>Lab</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSubmissions.map((submission) => (
                  <Tr key={submission.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold">{submission.patient_name}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {submission.patient_email}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{submission.doctor_name || 'N/A'}</Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="bold">
                          {submission.tests?.name || 'N/A'}
                        </Text>
                        {submission.tests?.cash_price && (
                          <Text fontSize="xs" color="green.600">
                            ${submission.tests.cash_price}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {submission.phlebotomist_profiles?.company_name || 'N/A'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(submission.status)}>
                        {statusConfig[submission.status]?.label || submission.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {new Date(submission.submitted_at).toLocaleDateString()}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(submission.submitted_at).toLocaleTimeString()}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="View Details">
                          <IconButton
                            icon={<ViewIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleViewDetails(submission)}
                          />
                        </Tooltip>
                        <Tooltip label="Change Status">
                          <Select
                            size="sm"
                            value={submission.status || 'pending'}
                            onChange={(e) => handleStatusChange(submission.id, e.target.value)}
                            w="120px"
                          >
                            {Object.entries(statusConfig).map(([status, config]) => (
                              <option key={status} value={status}>
                                {config.label}
                              </option>
                            ))}
                          </Select>
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Submission Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Submission Details - {selectedSubmission?.patient_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedSubmission && (
              <VStack spacing={6} align="stretch">
                {/* Patient Information */}
                <Box>
                  <Heading size="md" mb={3}>Patient Information</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Name:</Text>
                      <Text>{selectedSubmission.patient_name}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Email:</Text>
                      <Text>{selectedSubmission.patient_email || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">DOB:</Text>
                      <Text>{selectedSubmission.patient_dob || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Address:</Text>
                      <Text>{selectedSubmission.patient_address}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Doctor Information */}
                <Box>
                  <Heading size="md" mb={3}>Doctor Information</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Name:</Text>
                      <Text>{selectedSubmission.doctor_name || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Phone:</Text>
                      <Text>{selectedSubmission.doctor_phone || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Email:</Text>
                      <Text>{selectedSubmission.doctor_email || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Address:</Text>
                      <Text>{selectedSubmission.doctor_address || 'N/A'}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Test Information */}
                <Box>
                  <Heading size="md" mb={3}>Test Information</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Test:</Text>
                      <Text>{selectedSubmission.tests?.name || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Price:</Text>
                      <Text>${selectedSubmission.tests?.cash_price || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Collection Time:</Text>
                      <Text>{selectedSubmission.blood_collection_time || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">STAT Test:</Text>
                      <Text>{selectedSubmission.stat_test ? 'Yes' : 'No'}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Insurance Information */}
                {selectedSubmission.insurances && (
                  <Box>
                    <Heading size="md" mb={3}>Insurance Information</Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontWeight="bold">Provider:</Text>
                        <Text>{selectedSubmission.insurances.name}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">Type:</Text>
                        <Text>{selectedSubmission.insurances.type}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">Policy Number:</Text>
                        <Text>{selectedSubmission.policy_number || 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold">Group Number:</Text>
                        <Text>{selectedSubmission.group_number || 'N/A'}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}

                <Divider />

                {/* Lab Information */}
                <Box>
                  <Heading size="md" mb={3}>Lab Information</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Lab:</Text>
                      <Text>{selectedSubmission.phlebotomist_profiles?.company_name || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Phlebotomist:</Text>
                      <Text>{selectedSubmission.phlebotomist_profiles?.full_name || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Email:</Text>
                      <Text>{selectedSubmission.phlebotomist_profiles?.email || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Phone:</Text>
                      <Text>{selectedSubmission.phlebotomist_profiles?.phone || 'N/A'}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Submission Details */}
                <Box>
                  <Heading size="md" mb={3}>Submission Details</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Status:</Text>
                      <Badge colorScheme={getStatusColor(selectedSubmission.status)}>
                        {statusConfig[selectedSubmission.status]?.label || selectedSubmission.status}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Submitted:</Text>
                      <Text>{new Date(selectedSubmission.submitted_at).toLocaleString()}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">FedEx Label:</Text>
                      <Text>{selectedSubmission.need_fedex_label ? 'Yes' : 'No'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Special Instructions:</Text>
                      <Text>{selectedSubmission.special_instructions || 'None'}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                navigate(`/admin/submissions/${selectedSubmission?.id}`);
                onClose();
              }}
            >
              View Full Details
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BloodDrawDashboard;
